import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useTheme,
} from '@mui/material';
import Footer from '../components/Footer';
import {
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Euro as EuroIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function Dashboard() {
  const [stats, setStats] = useState({
    clients: 0,
    articles: 0,
    factures: 0,
    totalCA: 0,
  });
  const [recentFactures, setRecentFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [clientsRes, articlesRes, facturesRes] = await Promise.all([
        axios.get('/api/clients'),
        axios.get('/api/articles'),
        axios.get('/api/factures'),
      ]);

      const totalCA = facturesRes.data.reduce((sum, facture) => sum + facture.total_ttc, 0);

      setStats({
        clients: clientsRes.data.length,
        articles: articlesRes.data.length,
        factures: facturesRes.data.length,
        totalCA,
      });

      setRecentFactures(facturesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFacture = (id) => {
    navigate(`/factures/${id}`);
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await axios.get(`/api/factures/${id}/pdf`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {typeof value === 'number' && title.includes('CA') 
                ? `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`
                : value.toLocaleString('fr-FR')
              }
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 8 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
        Tableau de bord
      </Typography>

      {/* Statistiques */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Clients"
            value={stats.clients}
            icon={<PeopleIcon sx={{ color: 'white' }} />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Articles"
            value={stats.articles}
            icon={<InventoryIcon sx={{ color: 'white' }} />}
            color={theme.palette.secondary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Factures"
            value={stats.factures}
            icon={<ReceiptIcon sx={{ color: 'white' }} />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Chiffre d'affaires"
            value={stats.totalCA}
            icon={<EuroIcon sx={{ color: 'white' }} />}
            color="#ff9800"
          />
        </Grid>
      </Grid>

      {/* Dernières factures */}
      <Paper sx={{ width: '100%', overflow: 'hidden', flexGrow: 1 }}>
        <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white' }}>
          <Typography variant="h6">Dernières factures</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Numéro</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Montant</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentFactures.map((facture) => (
                <TableRow key={facture.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {facture.numero}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {facture.client_prenom ? `${facture.client_prenom} ${facture.client_nom}` : facture.client_nom}
                  </TableCell>
                  <TableCell>
                    {format(new Date(facture.date_facture + 'T00:00:00'), 'dd/MM/yyyy', { locale: fr })}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {facture.total_ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={facture.statut}
                      color={facture.statut === 'payée' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleViewFacture(facture.id)}
                      title="Voir la facture"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadPDF(facture.id)}
                      title="Télécharger PDF"
                    >
                      <GetAppIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Footer avec mention Woud */}
      <Footer />
    </Box>
  );
}

export default Dashboard; 